import * as React from 'react';

import { useDebouncedValue } from '../../../hooks';

import { VirtualQwertyKeyboardHandle } from './VirtualQwertyKeyboard';

export const VirtualKeyboardContext = React.createContext<{
  setValue: (value: string) => void;
  showKeyboard: () => void;
  hideKeyboard: () => void;
  setChangeListener: (listener: (value: string) => void) => void;
}>({
  setValue: () => {
    /* empty */
  },
  showKeyboard: () => {
    /* empty */
  },
  hideKeyboard: () => {
    /* empty */
  },
  setChangeListener: () => {
    /* empty */
  },
});

export const VirtualKeyboardProvider = VirtualKeyboardContext.Provider;

export const useVirtualKeyboardProvider = (touchModeEnabled: boolean) => {
  const latestKeyboardValueRef = React.useRef('');
  const keyboardRef = React.useRef<VirtualQwertyKeyboardHandle>(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = React.useState(false);
  const keyboardChangeListener = React.useRef<((value: string) => void) | null>(
    null,
  );

  const shouldRenderKeyboard = useDebouncedValue(
    touchModeEnabled && isKeyboardVisible,
    50,
  );

  const showKeyboard = React.useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = React.useCallback(() => setIsKeyboardVisible(false), []);
  const setKeyboardChangeListener = React.useCallback(
    (listener: (value: string) => void) => {
      keyboardChangeListener.current = listener;
    },
    [],
  );

  const setKeyboardValue = React.useCallback((value: string) => {
    latestKeyboardValueRef.current = value;
    keyboardRef.current?.setInput(value);
  }, []);

  React.useLayoutEffect(() => {
    if (shouldRenderKeyboard) {
      setTimeout(() => {
        keyboardRef.current?.setInput(latestKeyboardValueRef.current);
      }, 100);
    }
  }, [shouldRenderKeyboard]);

  return {
    keyboardRef,
    keyboardChangeListener,
    shouldRenderKeyboard,
    setValue: setKeyboardValue,
    showKeyboard,
    hideKeyboard,
    setChangeListener: setKeyboardChangeListener,
  };
};
